import type { TypeCode, DimensionId } from './types'
import { DIMENSION_INFO } from './interpretations'

// ===== 类型定义 =====

export type RelationshipType = '高度相似' | '天然契合' | '互补搭配' | '需要磨合' | '截然不同'

export interface DimensionComparisonItem {
  dimension: DimensionId
  dimensionName: string
  typeALetter: string
  typeBLetter: string
  typeAPosition: string
  typeBPosition: string
  isSame: boolean
}

export interface TypeComparison {
  typeA: TypeCode
  typeB: TypeCode

  // 维度对比
  dimensionComparison: DimensionComparisonItem[]

  // 相同维度数量
  sameCount: number

  // 相处模式
  relationshipType: RelationshipType
  relationshipAnalysis: string

  // 沟通建议
  adviceForA: string[]
  adviceForB: string[]

  // 共同点与差异点
  commonPoints: string[]
  differences: string[]
}

// ===== 维度位置映射 =====

const DIMENSION_SHORT: Record<DimensionId, { A: string; B: string }> = {
  D1: { A: '内部', B: '外部' },
  D2: { A: '系统', B: '情境' },
  D3: { A: '内在', B: '外在' },
  D4: { A: '闭合', B: '探索' },
}

// ===== 差异分析模板 =====

interface DifferenceAnalysis {
  analysis: string
  adviceForFirst: string
  adviceForSecond: string
  commonIfSame: string
}

const DIMENSION_DIFFERENCE_ANALYSIS: Record<DimensionId, {
  firstHasA: DifferenceAnalysis  // 第一个类型是A极，第二个是B极
  firstHasB: DifferenceAnalysis  // 第一个类型是B极，第二个是A极
}> = {
  D1: {
    firstHasA: {
      analysis: '你们在判断标准和反馈需求上存在差异。一方倾向于依据内在标准做判断，另一方更重视外部反馈来校准方向。',
      adviceForFirst: '对方需要更多外部反馈来确认方向。你觉得「不需要说」的认可，对TA来说可能是「没有回应」。尝试更主动地表达你的看法和反馈。',
      adviceForSecond: '对方有稳定的内在标准，不需要频繁确认。TA的沉默不代表否定，可能只是在思考。给TA一些独立空间，不必担心缺少回应。',
      commonIfSame: '你们都倾向于依据内在标准做判断，有相似的自我评价方式。',
    },
    firstHasB: {
      analysis: '你们在判断标准和反馈需求上存在差异。一方更重视外部反馈，另一方倾向于依据内在标准。',
      adviceForFirst: '对方有稳定的内在标准，不需要频繁确认。TA的沉默不代表否定，给TA一些独立思考的空间。',
      adviceForSecond: '对方需要更多外部反馈来确认方向。尝试更主动地表达你的看法，让TA知道你的想法。',
      commonIfSame: '你们都重视外部反馈和他人意见，善于从互动中获取信息。',
    },
  },
  D2: {
    firstHasA: {
      analysis: '你们在处理问题的方式上有所不同。一方偏好系统化、结构化的方法，另一方更擅长灵活应变、因地制宜。',
      adviceForFirst: '对方更习惯灵活应对，可能觉得过多的框架是束缚。在需要快速反应的场合，可以尝试信任TA的直觉判断。',
      adviceForSecond: '对方需要先建立框架再行动。在重要决策时，给TA一些时间梳理思路，不要催促TA「先做再说」。',
      commonIfSame: '你们都偏好系统化的思维方式，善于建立框架和流程。',
    },
    firstHasB: {
      analysis: '你们在处理问题的方式上有所不同。一方更擅长灵活应变，另一方偏好系统化的方法。',
      adviceForFirst: '对方需要先建立框架再行动。在讨论问题时，尝试用更有条理的方式表达你的想法。',
      adviceForSecond: '对方更习惯灵活应对。在需要快速反应的场合，可以信任TA的直觉判断，不必事事都要有完整计划。',
      commonIfSame: '你们都擅长灵活应变，能够根据情境调整策略。',
    },
  },
  D3: {
    firstHasA: {
      analysis: '你们的驱动力来源不同。一方更多被内在兴趣和意义感驱动，另一方更关注外在成果和认可。',
      adviceForFirst: '对方关注实际回报和成果认可。当你沉浸于某件事时，偶尔也分享一下「这件事有什么用」，帮助TA理解你的投入。',
      adviceForSecond: '对方追求的是过程中的意义感。不要总是问「这有什么用」，有时候「有意思」本身就是价值。',
      commonIfSame: '你们都被内在兴趣驱动，看重事情本身的意义而非外部回报。',
    },
    firstHasB: {
      analysis: '你们的驱动力来源不同。一方更关注外在成果，另一方更被内在意义驱动。',
      adviceForFirst: '对方追求的是过程中的意义感。尝试理解TA对某些事情的热情，即使它们看起来「没什么用」。',
      adviceForSecond: '对方关注实际回报和成果认可。在合作时，可以明确讨论目标和预期收益，这会让TA更有动力。',
      commonIfSame: '你们都关注外在成果和认可，有相似的目标导向。',
    },
  },
  D4: {
    firstHasA: {
      analysis: '你们对不确定性的态度不同。一方倾向于尽快确定方向，另一方更愿意保持开放、继续探索。',
      adviceForFirst: '对方享受探索的过程，过早锁定可能让TA感到受限。在非紧急情况下，可以给TA更多探索的空间。',
      adviceForSecond: '对方需要确定性来安心。当你还想继续探索时，可以先给TA一个阶段性的结论，让TA知道大方向。',
      commonIfSame: '你们都倾向于快速做出决定，追求确定性和完成感。',
    },
    firstHasB: {
      analysis: '你们对不确定性的态度不同。一方更愿意保持开放，另一方倾向于尽快确定方向。',
      adviceForFirst: '对方需要确定性来安心。当讨论开放性问题时，可以先给出你当前的倾向，再说明还在思考的部分。',
      adviceForSecond: '对方享受探索的过程。不要催促TA立即做决定，给TA一些时间去探索不同可能性。',
      commonIfSame: '你们都对新事物保持开放态度，享受探索和尝试的过程。',
    },
  },
}

// ===== 关系类型描述 =====

const RELATIONSHIP_DESCRIPTIONS: Record<RelationshipType, string> = {
  '高度相似': '你们在所有维度上都有相同的倾向，认知风格高度一致。这意味着你们很容易理解彼此的想法和行为方式，沟通成本低。但也要注意，相似可能意味着盲区也相似，需要共同注意。',
  '天然契合': '你们在大多数维度上有相同的倾向，容易建立默契。少量的差异可以带来互补，而不会造成根本性的摩擦。',
  '互补搭配': '你们在一半的维度上有所不同，这种差异可以形成互补。一方的优势可以弥补另一方的不足，但也需要相互理解和包容。',
  '需要磨合': '你们在多数维度上存在差异，认知风格差别较大。这不意味着不能相处，而是需要更多的沟通和理解。差异本身不是问题，不理解差异才是。',
  '截然不同': '你们在所有维度上都有不同的倾向，认知风格形成鲜明对比。这既是挑战也是机会——你们可以从完全不同的视角看问题，但也需要格外注意沟通方式。',
}

// ===== 核心对比函数 =====

export function compareTypes(typeA: TypeCode, typeB: TypeCode): TypeComparison {
  const dimensions: DimensionId[] = ['D1', 'D2', 'D3', 'D4']
  const typeALetters = typeA.split('')
  const typeBLetters = typeB.split('')

  // 构建维度对比
  const dimensionComparison: DimensionComparisonItem[] = dimensions.map((dim, index) => {
    const dimInfo = DIMENSION_INFO[dim]
    const aLetter = typeALetters[index]
    const bLetter = typeBLetters[index]
    const isSame = aLetter === bLetter

    const isAFirst = aLetter === dimInfo.poleA.letter
    const isBFirst = bLetter === dimInfo.poleA.letter

    return {
      dimension: dim,
      dimensionName: dimInfo.name,
      typeALetter: aLetter,
      typeBLetter: bLetter,
      typeAPosition: isAFirst ? DIMENSION_SHORT[dim].A : DIMENSION_SHORT[dim].B,
      typeBPosition: isBFirst ? DIMENSION_SHORT[dim].A : DIMENSION_SHORT[dim].B,
      isSame,
    }
  })

  // 计算相同维度数量
  const sameCount = dimensionComparison.filter(d => d.isSame).length

  // 确定关系类型
  let relationshipType: RelationshipType
  if (sameCount === 4) {
    relationshipType = '高度相似'
  } else if (sameCount === 3) {
    relationshipType = '天然契合'
  } else if (sameCount === 2) {
    relationshipType = '互补搭配'
  } else if (sameCount === 1) {
    relationshipType = '需要磨合'
  } else {
    relationshipType = '截然不同'
  }

  // 生成关系分析
  const relationshipAnalysis = RELATIONSHIP_DESCRIPTIONS[relationshipType]

  // 生成沟通建议和共同点/差异点
  const adviceForA: string[] = []
  const adviceForB: string[] = []
  const commonPoints: string[] = []
  const differences: string[] = []

  dimensionComparison.forEach((comp, index) => {
    const dim = comp.dimension
    const dimInfo = DIMENSION_INFO[dim]
    const aLetter = typeALetters[index]

    if (comp.isSame) {
      // 共同点
      const isAFirst = aLetter === dimInfo.poleA.letter
      const analysis = DIMENSION_DIFFERENCE_ANALYSIS[dim]
      commonPoints.push(isAFirst ? analysis.firstHasA.commonIfSame : analysis.firstHasB.commonIfSame)
    } else {
      // 差异点
      const isAFirst = aLetter === dimInfo.poleA.letter
      const analysis = DIMENSION_DIFFERENCE_ANALYSIS[dim]
      const template = isAFirst ? analysis.firstHasA : analysis.firstHasB

      differences.push(template.analysis)
      adviceForA.push(template.adviceForFirst)
      adviceForB.push(template.adviceForSecond)
    }
  })

  return {
    typeA,
    typeB,
    dimensionComparison,
    sameCount,
    relationshipType,
    relationshipAnalysis,
    adviceForA,
    adviceForB,
    commonPoints,
    differences,
  }
}

// ===== 辅助函数 =====

export function getRelationshipColor(type: RelationshipType): string {
  switch (type) {
    case '高度相似': return '#10b981'  // green
    case '天然契合': return '#3b82f6'  // blue
    case '互补搭配': return '#8b5cf6'  // purple
    case '需要磨合': return '#f59e0b'  // amber
    case '截然不同': return '#ef4444'  // red
  }
}
